import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, X, ChevronDown, ChevronUp, Upload, Shield, Save, Share2, Users, Sparkles } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

type StepState = 'idle' | 'pending' | 'in_progress' | 'success' | 'error';

interface Step {
  key: string;
  label: string;
  state: StepState;
  detail?: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

interface PostFlowOverlayProps {
  open: boolean;
  mode?: 'full' | 'sheet';
  onClose?: () => void;
  onMinimize?: () => void;
}

// Lightweight event-based updates so we don't tightly couple to form hook
// Dispatch CustomEvent<'post-flow:update'> with detail: Partial<Step>[] or single step update
// Example: window.dispatchEvent(new CustomEvent('post-flow:update', { detail: { key: 'upload', state: 'in_progress', detail: '25%' } }))

const BASE_STEPS: Step[] = [
  { 
    key: 'upload', 
    label: 'Preparing Content', 
    state: 'idle',
    icon: Upload,
    description: 'Preparing your content for the constellation'
  },
  { 
    key: 'moderation', 
    label: 'Scanning Content', 
    state: 'idle',
    icon: Shield,
    description: 'Ensuring your content meets community standards'
  },
  { 
    key: 'save', 
    label: 'Publishing to Feed', 
    state: 'idle',
    icon: Save,
    description: 'Adding your content to your personal feed'
  },
  { 
    key: 'crosspost', 
    label: 'Sharing to Community', 
    state: 'idle',
    icon: Share2,
    description: 'Broadcasting to your selected communities'
  },
  { 
    key: 'communities', 
    label: 'Finding Communities', 
    state: 'idle',
    icon: Users,
    description: 'Discovering relevant community connections'
  },
  { 
    key: 'constellations', 
    label: 'Creating Connections', 
    state: 'idle',
    icon: Sparkles,
    description: 'Forming constellation links with related content'
  },
];

export const PostFlowOverlay: React.FC<PostFlowOverlayProps> = ({ open, mode = 'full', onClose, onMinimize }) => {
  const [steps, setSteps] = React.useState<Step[]>(BASE_STEPS);
  const [expanded, setExpanded] = React.useState(true);

  React.useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<any>;
      const update = ce.detail;
      setSteps(prev => {
        const next = [...prev];
        const apply = (u: Partial<Step>) => {
          const idx = next.findIndex(s => s.key === u.key);
          if (idx !== -1) {
            next[idx] = { ...next[idx], ...u } as Step;
          }
        };
        if (Array.isArray(update)) update.forEach(apply); else apply(update);
        return next;
      });
    };
    window.addEventListener('post-flow:update', handler as EventListener);
    return () => window.removeEventListener('post-flow:update', handler as EventListener);
  }, []);

  // Calculate overall progress
  const completedSteps = steps.filter(s => s.state === 'success').length;
  const totalSteps = steps.length;
  const overallProgress = (completedSteps / totalSteps) * 100;
  const currentStep = steps.find(s => s.state === 'in_progress') || steps.find(s => s.state === 'pending');
  const hasError = steps.some(s => s.state === 'error');
  const isComplete = completedSteps === totalSteps;

  // Floating particles component
  const FloatingParticles = () => (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: 12 }).map((_, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute w-1 h-1 bg-primary rounded-full opacity-60"
          initial={{
            x: Math.random() * 400,
            y: Math.random() * 400,
            scale: Math.random() * 0.5 + 0.5,
          }}
          animate={{
            y: [0, -20, 0],
            x: [0, Math.random() * 10 - 5, 0],
            opacity: [0.3, 0.8, 0.3],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`fixed inset-0 z-[70] ${mode === 'sheet' ? 'pointer-events-none' : ''}`}
        >
          {/* Constellation background */}
          {mode === 'full' && (
            <div className="absolute inset-0 bg-gradient-to-br from-background via-background/95 to-background/90">
              <FloatingParticles />
            </div>
          )}

          <div className={mode === 'full'
            ? 'relative mx-auto mt-8 w-full max-w-2xl'
            : 'pointer-events-auto fixed bottom-0 left-0 right-0 mx-auto w-full max-w-xl'}>
            
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className={`relative bg-gradient-to-br from-card/90 to-card/80 backdrop-blur-xl border border-primary/20 shadow-2xl ${
                mode === 'full' ? 'rounded-3xl' : 'rounded-t-3xl'
              }`}
              style={{
                boxShadow: '0 25px 50px -12px rgba(139, 92, 246, 0.25)',
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-primary/10">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Sparkles className="w-6 h-6 text-primary" />
                    <motion.div
                      className="absolute -inset-2 rounded-full bg-primary/20"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.6, 0.3],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                      }}
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                      Creating Your Constellation
                    </h2>
                    {currentStep && (
                      <p className="text-sm text-muted-foreground">{currentStep.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {mode === 'full' && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={onMinimize}
                      className="text-xs px-4 py-2 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20"
                    >
                      Continue in background
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>

              {/* Progress Section */}
              <div className="p-6 space-y-6">
                {/* Overall Progress */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-foreground">
                      {isComplete ? 'Complete!' : hasError ? 'Error occurred' : `Step ${completedSteps + 1} of ${totalSteps}`}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {Math.round(overallProgress)}%
                    </span>
                  </div>
                  <div className="relative">
                    <Progress 
                      value={overallProgress} 
                      className={`h-3 bg-muted/50 ${hasError ? '[&>div]:bg-destructive' : '[&>div]:bg-gradient-to-r [&>div]:from-primary [&>div]:to-secondary'}`}
                    />
                    {!isComplete && !hasError && (
                      <motion.div
                        className="absolute top-0 left-0 h-3 w-8 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                        animate={{
                          x: [-32, 400],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        style={{ borderRadius: 'inherit' }}
                      />
                    )}
                  </div>
                </div>

                {/* Steps */}
                {expanded && (
                  <div className="space-y-4">
                    {steps.map((step, index) => {
                      const StepIcon = step.icon;
                      const isCompleted = step.state === 'success';
                      const isActive = step.state === 'in_progress' || step.state === 'pending';
                      const hasStepError = step.state === 'error';
                      
                      return (
                        <motion.div
                          key={step.key}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`relative flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 ${
                            isCompleted 
                              ? 'bg-primary/5 border-primary/30 shadow-lg shadow-primary/10' 
                              : isActive 
                                ? 'bg-secondary/5 border-secondary/30 shadow-lg shadow-secondary/10'
                                : hasStepError
                                  ? 'bg-destructive/5 border-destructive/30'
                                  : 'bg-muted/5 border-muted/20'
                          }`}
                        >
                          {/* Connection line to next step */}
                          {index < steps.length - 1 && (
                            <div 
                              className={`absolute left-8 top-16 w-0.5 h-8 transition-colors ${
                                isCompleted ? 'bg-primary/50' : 'bg-muted/20'
                              }`} 
                            />
                          )}
                          
                          {/* Step Icon */}
                          <div className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                            isCompleted 
                              ? 'bg-primary/20 border-primary text-primary' 
                              : isActive 
                                ? 'bg-secondary/20 border-secondary text-secondary'
                                : hasStepError
                                  ? 'bg-destructive/20 border-destructive text-destructive'
                                  : 'bg-muted/10 border-muted/30 text-muted-foreground'
                          }`}>
                            {isCompleted ? (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.2 }}
                              >
                                <Check className="w-5 h-5" />
                              </motion.div>
                            ) : isActive ? (
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                              >
                                <StepIcon className="w-5 h-5" />
                              </motion.div>
                            ) : hasStepError ? (
                              <X className="w-5 h-5" />
                            ) : (
                              <StepIcon className="w-5 h-5 opacity-50" />
                            )}
                            
                            {/* Pulsing ring for active state */}
                            {isActive && (
                              <motion.div
                                className="absolute inset-0 rounded-full border-2 border-secondary"
                                animate={{
                                  scale: [1, 1.3, 1],
                                  opacity: [0.5, 0, 0.5],
                                }}
                                transition={{
                                  duration: 1.5,
                                  repeat: Infinity,
                                }}
                              />
                            )}
                          </div>

                          {/* Step Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className={`font-medium ${
                                isCompleted ? 'text-primary' : isActive ? 'text-secondary' : 'text-muted-foreground'
                              }`}>
                                {step.label}
                              </h3>
                              {step.detail && (
                                <span className="text-xs px-2 py-1 rounded-full bg-muted/20 text-muted-foreground">
                                  {step.detail}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {step.description}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}

                {/* Toggle Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setExpanded(!expanded)}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {expanded ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show Details
                    </>
                  )}
                </motion.button>

                {/* Connected Content Preview */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    Constellation Preview
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.1 }}
                        className="aspect-video rounded-lg bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/10 relative overflow-hidden"
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent"
                          animate={{
                            x: [-100, 100],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: i * 0.2,
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-primary/50" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

export default PostFlowOverlay;
